import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  SectionContainer,
  StickyCTABar,
  transformMillisecToSec,
} from "component-library";
import { useTranslation } from "i18n";
import {
  captureExceptionEvent,
  FEATURE_FLAGS,
  useLaunchDarklyFlags,
} from "shared-services";
import { dataTestIds } from "data-testids";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import { BASE_PATH, MEDIA_FULL_PATH } from "app/config/routes";
import { AddMediaForm } from "./components/AddMediaForm";
import { GlobalEventTypes } from "contexts/GlobalContext";
import mediaService from "mediaModules/media/services/MediaService";
import {
  MediaAssetType,
  MediaMetadataPayload,
  MediaStorageSpace,
} from "mediaModules/media/models";
import { scrollRefToTop } from "mediaModules/media/utils/page";
import {
  heapTrackingForAddNewAudiPage,
  heapTrackingForAddNewVideoPage,
} from "app/helpers/heapTracking";
import {
  getAlertMessage,
  getSectionContainerProps,
  getStoreLimitAlert,
  PopupTypes,
} from "./AddMediaPageContent";
import "./AddMediaPage.scss";

export interface IAddMediaPageProps extends IGlobalContextProps {}

const getInitialStorageByType = (): Record<
  MediaAssetType,
  MediaStorageSpace
> => ({
  [MediaAssetType.Audio]: {
    allowedStorageSpace: {
      maxAllowedStoreInSec: 0,
      maxAllowedDisplayInSec: 0,
    },
    usedStorageSpace: {
      usedStoreValueInSec: 0,
      usedDisplayValueInSec: 0,
    },
  },
  [MediaAssetType.Video]: {
    allowedStorageSpace: {
      maxAllowedStoreInSec: 0,
      maxAllowedDisplayInSec: 0,
    },
    usedStorageSpace: {
      usedStoreValueInSec: 0,
      usedDisplayValueInSec: 0,
    },
  },
});

export const AddMediaPage = ({
  globalContext,
}: PropsWithChildren<IAddMediaPageProps>) => {
  const navigate = useNavigate();
  const {
    [FEATURE_FLAGS.ProfileRedesign]: isProfileRedesignEnabled,
    [FEATURE_FLAGS.ProfileMediaPageConsolidation]:
      isProfileMediaPageConsolidationEnabled,
  } = useLaunchDarklyFlags([
    FEATURE_FLAGS.ProfileRedesign,
    FEATURE_FLAGS.ProfileMediaPageConsolidation,
  ]);
  const pageRef = useRef<HTMLDivElement>(null);
  const submitRef = useRef<(() => void) | null>(null);
  const [metadataPayload, setMetadataPayload] = useState(
    new MediaMetadataPayload()
  );
  const [storageSpaceByType, setStorageSpaceByType] = useState(
    getInitialStorageByType()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const { t } = useTranslation();

  const closeGlobalAlert = () =>
    globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert);

  const redirectTo = () => {
    scrollRefToTop(pageRef);
    navigate(MEDIA_FULL_PATH);
  };

  const deleteMediaObject = async (mediaInfoId: string) => {
    if (mediaInfoId) {
      try {
        await mediaService.deleteMediaObjectById(mediaInfoId);
      } catch (error) {
        captureExceptionEvent(error);
      }
    }
  };

  const closePage = () => {
    const isMetadataPayloadChanged = Boolean(
      metadataPayload.title || metadataPayload.mediaInfoId
    );

    if (isMetadataPayloadChanged) {
      const message = getAlertMessage(
        t,
        PopupTypes.CancelClose,
        closeGlobalAlert,
        async () => {
          closeGlobalAlert();
          await deleteMediaObject(metadataPayload.mediaInfoId);
          redirectTo();
        }
      );

      globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );
    } else {
      redirectTo();
    }
  };

  const handleSaveClick = (submit: () => void) => {
    submit();
  };

  const handleCancelAndCloseClick = () => {
    closePage();
  };

  const handleCloseClick = () => {
    closePage();
  };

  const saveMetadata = async () => {
    if (metadataPayload.mediaInfoId) {
      setIsLoading(true);
      try {
        await mediaService.createMediaMetadata(metadataPayload);
        redirectTo();
      } catch (error) {
        setIsLoading(false);
        captureExceptionEvent(error);
      }
    }
  };

  const showDeletePopup = (updatedPayload: MediaMetadataPayload, callback) => {
    const message = getAlertMessage(
      t,
      PopupTypes.Delete,
      closeGlobalAlert,
      async () => {
        closeGlobalAlert();
        setMetadataPayload(updatedPayload);
        await deleteMediaObject(metadataPayload.mediaInfoId);
        callback();
      }
    );
    globalContext.notifyListener(
      GlobalEventTypes.notifyingGlobalAlert,
      message
    );
  };

  const preUploadValidation = (
    durationInMs: number,
    mediaType: MediaAssetType = MediaAssetType.Video
  ) => {
    const storageSpace = storageSpaceByType[mediaType];
    const {
      usedStorageSpace: { usedStoreValueInSec },
      allowedStorageSpace: { maxAllowedStoreInSec },
    } = storageSpace;
    const durationInSecAfterAdding =
      transformMillisecToSec(durationInMs) + usedStoreValueInSec;

    if (durationInSecAfterAdding > maxAllowedStoreInSec) {
      const message = getStoreLimitAlert(t, closeGlobalAlert);
      globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );

      return false;
    }

    return true;
  };

  useEffect(() => {
    const loadStorage = async () => {
      try {
        const [storageByType, audioAssets, videoAssets] = await Promise.all([
          mediaService.getAllowedStorageSpace(),
          mediaService.getMediaAssets(MediaAssetType.Audio, closeGlobalAlert),
          mediaService.getMediaAssets(MediaAssetType.Video, closeGlobalAlert),
        ]);

        setStorageSpaceByType({
          [MediaAssetType.Audio]: {
            allowedStorageSpace: storageByType[MediaAssetType.Audio],
            usedStorageSpace: mediaService.getUsedStorageSpace(audioAssets),
          },
          [MediaAssetType.Video]: {
            allowedStorageSpace: storageByType[MediaAssetType.Video],
            usedStorageSpace: mediaService.getUsedStorageSpace(videoAssets),
          },
        });
      } catch (error) {
        captureExceptionEvent(error);
      }
    };

    loadStorage();
  }, []);

  const handleSubmitFromFooter = () => {
    const submit = submitRef.current;

    if (!submit) {
      return;
    }

    const mediaType = metadataPayload.mediaType || MediaAssetType.Video;

    if (mediaType === MediaAssetType.Audio) {
      heapTrackingForAddNewAudiPage(isProfileRedesignEnabled);
    } else {
      heapTrackingForAddNewVideoPage(isProfileRedesignEnabled);
    }

    handleSaveClick(submit);
  };

  const registerFormSubmit = (submit: () => void) => {
    submitRef.current = submit;

    return null;
  };

  const sectionContainerProps = getSectionContainerProps(t, handleCloseClick);

  if (!isProfileMediaPageConsolidationEnabled) {
    return <Navigate to={BASE_PATH} replace />;
  }

  return (
    <>
      <div ref={pageRef}>
        <Breadcrumbs
          content={{
            homePageName: t("performer:accountSettings.breadcrumb.account"),
            videoPageName: t("common:nav.links.video"),
            audioPageName: t("common:nav.links.audio"),
            usernamePostfix: "",
          }}
        />
        <div className="g-content-area p-add-new-media-page">
          <div className="g-col-lg-12 p-add-new-media-page__container">
            <SectionContainer {...sectionContainerProps}>
              <div className="p-add-new-media-page__content">
                <AddMediaForm
                  storageSpaceByType={storageSpaceByType}
                  uploaderContent={{
                    dropzone: t(
                      "performer:media.addNew.form.uploader.dropzone"
                    ),
                    dropzoneMobile: t(
                      "performer:media.addNew.form.uploader.dropzoneMobile"
                    ),
                    uploadFileError: t(
                      "performer:media.addNew.form.uploader.uploadFileError"
                    ),
                    formatValidationError: t(
                      "performer:media.addNew.form.uploader.formatValidationError"
                    ),
                    removeFileButton: t(
                      "performer:media.addNew.form.uploader.removeFileButton"
                    ),
                    retryUploadButton: t(
                      "performer:media.addNew.form.uploader.retryUploadButton"
                    ),
                  }}
                  titleRequiredError={t(
                    "performer:media.addNew.form.titleRequiredError"
                  )}
                  mediaRequiredError={t(
                    "performer:media.addNew.form.mediaRequiredError"
                  )}
                  titleLabel={t("performer:media.addNew.form.titleLabel")}
                  titlePlaceholder={t(
                    "performer:media.addNew.form.titlePlaceholder"
                  )}
                  titleTooltip={{
                    content: t(
                      "performer:media.addNew.form.titleTooltip.content"
                    ),
                  }}
                  metadataPayload={metadataPayload}
                  updateMetadata={setMetadataPayload}
                  saveChanges={saveMetadata}
                  showDeletePopup={showDeletePopup}
                  preUploadValidation={preUploadValidation}
                  disableSaveButton={setIsFileUploading}
                >
                  {registerFormSubmit}
                </AddMediaForm>
              </div>
            </SectionContainer>
          </div>
        </div>
      </div>
      <StickyCTABar
        isSticky
        prev={{
          text: t("common:button.label.cancel"),
          onClick: handleCancelAndCloseClick,
          wrapperClassName: "mr-auto",
        }}
        next={{
          text: t("common:button.label.save"),
          onClick: handleSubmitFromFooter,
          disabled: isFileUploading,
          loading: isLoading,
          dataTestId: `${dataTestIds.componentLibrary["Atoms.Button.Link"]}-${dataTestIds.profile["Media.MediaForm.SaveButton"]}`,
        }}
        ctaBarAdditionalClassName="max-w-[1410px]"
      />
    </>
  );
};

export default withGlobalContext(AddMediaPage);
